import {
  RiAddLine,
  RiBarChart2Line,
  RiBookOpenLine,
  RiChatCheckLine,
  RiCloudLine,
  RiComputerLine,
  RiCpuLine,
  RiDatabase2Line,
  RiFileShield2Line,
  RiHome5Line,
  RiLayoutGridLine,
  RiLockLine,
  RiPuzzle2Line,
  RiRefreshLine,
  RiRobot2Line,
  RiUploadCloud2Line,
  RiShapesLine,
  RiShieldCheckLine,
  RiShipLine,
  type RemixiconComponentType,
} from '@remixicon/react';
import { configApiRef, useApi } from '@backstage/core-plugin-api';
import {
  DEVBOX_PATHS,
  DEVBOX_SUBMENU_ITEMS,
} from '@internal/backstage-plugin-devbox-manager';
import {
  VIBE_CODED_APPS_ROUTES,
  VIBE_CODED_APPS_SUBMENU_ITEMS,
} from '@internal/plugin-cloudflare-workers';
import {
  SOC2_REPORT_ROUTES,
  SOC2_REPORT_SUBMENU_ITEMS,
} from '@internal/plugin-soc2-report';
import {
  MAKE_SHELL_ROUTES,
  MAKE_SHELL_SUBMENU_ITEMS,
} from '@internal/plugin-make-shell';
import { DEPENDENCY_SECURITY_ROUTES } from '@internal/plugin-dependency-security';
import {
  MAKE_REVIEW_ROUTES,
  MAKE_REVIEW_SUBMENU_ITEMS,
} from '@internal/plugin-make-review';
import { getFlag } from '../../../helpers/helpers';
import { BLUEPRINTS_TABS } from '../../scaffolder/BlueprintsHeader';
import {
  CATALOG_PATHS,
  useCatalogTypeDestinations,
} from '../../catalog/catalogNavigation';
import { normalizeSidebarPath } from '../SidebarCustomization';

export type SidebarNavLink = { title: string; to: string };

export type SidebarNavSubmenu = {
  label?: string;
  items: readonly SidebarNavLink[];
};

export type SidebarNavGroup =
  | 'Platform'
  | 'Delivery'
  | 'Quality and security'
  | 'AI'
  | 'Reports';

export const SIDEBAR_NAV_GROUPS: readonly SidebarNavGroup[] = [
  'Platform',
  'Delivery',
  'Quality and security',
  'AI',
  'Reports',
];

export type SidebarNavItem = {
  /** Normalized path. Also the favourites key, shared with the legacy sidebar. */
  path: string;
  title: string;
  /** Label under the icon in narrow mode. */
  shortTitle: string;
  icon: RemixiconComponentType;
  group: SidebarNavGroup;
  submenu?: readonly SidebarNavSubmenu[];
};

// Same destinations and defaults as the legacy sidebar in Root.tsx, so a
// user's favourites carry over in both directions.
export const DEFAULT_SIDEBAR_FAVORITES = [
  '/',
  '/catalog',
  '/docs',
  '/deployments',
  '/devbox-manager',
  '/vibe-coded-apps',
  '/kargo',
  '/create',
];

// The legacy sidebar keeps Home in its own section above a divider.
// Passing the same sections keeps that layout intact when people switch back.
export const LEGACY_SIDEBAR_SECTIONS = (paths: readonly string[]) => [
  paths.filter(path => path === '/'),
  paths.filter(path => path !== '/'),
];

// The flyout's heading links to the item's own page, so submenus list only
// the pages underneath it.
const BLUEPRINTS_SUBMENU: readonly SidebarNavSubmenu[] = [
  {
    items: BLUEPRINTS_TABS.slice(1).map(tab => ({
      title: tab.label,
      to: tab.path,
    })),
  },
];

const CATALOG_ACTIONS: readonly SidebarNavLink[] = [
  { title: 'Create Component', to: '/create' },
];

const single = (
  items: readonly SidebarNavLink[],
): readonly SidebarNavSubmenu[] => [{ items }];

export const useSidebarNavItems = (): SidebarNavItem[] => {
  const config = useApi(configApiRef);
  const catalogTypes = useCatalogTypeDestinations();
  const flag = (name: string) => getFlag(config, name);

  const items: (SidebarNavItem | false)[] = [
    {
      path: '/',
      title: 'Home',
      shortTitle: 'Home',
      icon: RiHome5Line,
      group: 'Platform',
    },
    {
      path: CATALOG_PATHS.root,
      title: 'Catalog',
      shortTitle: 'Catalog',
      icon: RiShapesLine,
      group: 'Platform',
      submenu: catalogTypes.length
        ? [{ items: [...catalogTypes, ...CATALOG_ACTIONS] }]
        : undefined,
    },
    {
      path: '/docs',
      title: 'Docs',
      shortTitle: 'Docs',
      icon: RiBookOpenLine,
      group: 'Platform',
    },
    {
      path: '/create',
      title: 'Blueprints',
      shortTitle: 'Create',
      icon: RiAddLine,
      group: 'Platform',
      submenu: BLUEPRINTS_SUBMENU,
    },
    {
      path: '/api-docs',
      title: 'APIs',
      shortTitle: 'APIs',
      icon: RiPuzzle2Line,
      group: 'Platform',
    },
    flag('deployments') && {
      path: '/deployments',
      title: 'Deployments',
      shortTitle: 'Deploys',
      icon: RiUploadCloud2Line,
      group: 'Delivery',
    },
    flag('devbox-manager') && {
      path: DEVBOX_PATHS.root,
      title: 'Devboxes',
      shortTitle: 'Devboxes',
      icon: RiComputerLine,
      group: 'Delivery',
      submenu: single(DEVBOX_SUBMENU_ITEMS),
    },
    flag('vibe-coded-apps') && {
      path: VIBE_CODED_APPS_ROUTES.root.path,
      title: 'Internal Apps',
      shortTitle: 'Apps',
      icon: RiCloudLine,
      group: 'Delivery',
      submenu: single(VIBE_CODED_APPS_SUBMENU_ITEMS),
    },
    flag('kargo') && {
      path: '/kargo',
      title: 'Kargo',
      shortTitle: 'Kargo',
      icon: RiShipLine,
      group: 'Delivery',
    },
    flag('lock-manager') && {
      path: '/lock-manager',
      title: 'Locks',
      shortTitle: 'Locks',
      icon: RiLockLine,
      group: 'Delivery',
    },
    flag('make-shell') && {
      path: MAKE_SHELL_ROUTES.root.path,
      title: 'Make Shell',
      shortTitle: 'Shell',
      icon: RiLayoutGridLine,
      group: 'Delivery',
      submenu: single(MAKE_SHELL_SUBMENU_ITEMS),
    },
    flag('make-review') && {
      path: MAKE_REVIEW_ROUTES.overview.path,
      title: 'Make Review',
      shortTitle: 'Review',
      icon: RiChatCheckLine,
      group: 'Quality and security',
      submenu: single(MAKE_REVIEW_SUBMENU_ITEMS),
    },
    flag('dependency-security') && {
      path: DEPENDENCY_SECURITY_ROUTES.root.path,
      title: 'Dependency Security',
      shortTitle: 'Deps',
      icon: RiShieldCheckLine,
      group: 'Quality and security',
    },
    flag('soc2-report') && {
      path: SOC2_REPORT_ROUTES.root.path,
      title: 'SOC2 Report',
      shortTitle: 'SOC2',
      icon: RiFileShield2Line,
      group: 'Quality and security',
      submenu: single(SOC2_REPORT_SUBMENU_ITEMS),
    },
    flag('cloud-agents') && {
      path: '/cloud-agents',
      title: 'Cloud Agents',
      shortTitle: 'Agents',
      icon: RiRobot2Line,
      group: 'AI',
    },
    flag('context-router') && {
      path: '/context-router',
      title: 'Context Router',
      shortTitle: 'Context',
      icon: RiDatabase2Line,
      group: 'AI',
    },
    {
      path: '/ai-spend/reporting',
      title: 'AI Spend',
      shortTitle: 'AI spend',
      icon: RiBarChart2Line,
      group: 'Reports',
    },
    flag('node-inventory') && {
      path: '/node-inventory',
      title: 'Node Inventory',
      shortTitle: 'Nodes',
      icon: RiCpuLine,
      group: 'Reports',
    },
    flag('versions-manager') && {
      path: '/versions-manager',
      title: 'Version Manager',
      shortTitle: 'Versions',
      icon: RiRefreshLine,
      group: 'Reports',
    },
  ];

  return items
    .filter((item): item is SidebarNavItem => Boolean(item))
    .map(item => ({ ...item, path: normalizeSidebarPath(item.path) }));
};
